import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CreditCard, Link2 } from 'lucide-react';
import { isLscBillingUiEnabled } from '../../config/featureFlags';
import {
  bindLscPaidSubscriptionToLeague,
  getLscBillingStatus,
  LscApiError,
} from '../api';
import type { LscBillingStatusResponse } from '../types';
import { formatBindLeagueError } from '../entitlementBillingErrors';
import { LSC_SUBSCRIPTION_PRODUCT_NAME } from '../billingCopy';
import {
  buildLeaguePresentationInput,
  resolveLscBillingPresentation,
} from '../billingPresentation';
import BillingDisabledBanner from './BillingDisabledBanner';
import BillingStatusCard from './BillingStatusCard';
import BillingSupportContact from '../billingSupportContact';
import LeagueCheckoutActions from './LeagueCheckoutActions';
import LoadingState from './LoadingState';

interface LeagueBillingPanelProps {
  leagueId: string;
  leagueArchived?: boolean;
  isOwner?: boolean;
  isOperator?: boolean;
}

const LeagueBillingPanel: React.FC<LeagueBillingPanelProps> = ({
  leagueId,
  leagueArchived = false,
  isOwner = true,
  isOperator = true,
}) => {
  const billingUiEnabled = isLscBillingUiEnabled();
  const [billingStatus, setBillingStatus] = useState<LscBillingStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [bindLoading, setBindLoading] = useState(false);
  const [bindError, setBindError] = useState<string | null>(null);
  const [bindSuccess, setBindSuccess] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const status = await getLscBillingStatus(leagueId);
      setBillingStatus(status);
    } catch (err) {
      if (err instanceof LscApiError && (err.status === 401 || err.status === 403)) {
        setBillingStatus(null);
        setLoadError(null);
      } else {
        setLoadError('Billing information is unavailable right now.');
      }
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus]);

  const presentation = useMemo(
    () =>
      resolveLscBillingPresentation(
        buildLeaguePresentationInput(billingStatus, 'league_billing_settings', {
          isOwner,
          isOperator,
        }, {
          billingUiEnabled,
          leagueId,
          leagueArchived,
        })
      ),
    [billingStatus, billingUiEnabled, isOperator, isOwner, leagueArchived, leagueId]
  );

  const handleBind = async () => {
    setBindLoading(true);
    setBindError(null);
    setBindSuccess(false);
    try {
      const result = await bindLscPaidSubscriptionToLeague(leagueId);
      if (result.success) {
        setBindSuccess(true);
        await loadStatus();
        return;
      }
      setBindError('Could not bind subscription to this league. Please try again or contact support.');
    } catch (err) {
      setBindError(formatBindLeagueError(err));
    } finally {
      setBindLoading(false);
    }
  };

  if (loading) {
    return <LoadingState message="Loading league billing..." />;
  }

  return (
    <div className="card space-y-4" id="league-subscription" data-testid="lsc-league-billing-panel">
      <div className="flex items-start gap-3">
        <CreditCard className="h-5 w-5 text-primary-400 mt-0.5 shrink-0" aria-hidden />
        <div className="space-y-1 min-w-0">
          <h2 className="font-semibold text-white">
            {presentation.showStatusCard ? 'League subscription' : presentation.headline}
          </h2>
          <p className="text-sm text-secondary-400">{presentation.valueProposition}</p>
          {!presentation.showStatusCard && (
            <p className="text-sm text-secondary-500">{LSC_SUBSCRIPTION_PRODUCT_NAME}</p>
          )}
        </div>
      </div>

      {presentation.disabledBanner === 'ui_disabled' && (
        <BillingDisabledBanner variant="ui" message={presentation.disabledReason} />
      )}

      {presentation.mode === 'owner_managed' && (
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-900/50 px-4 py-3 space-y-2"
          data-testid="lsc-league-billing-owner-managed"
          role="status"
        >
          {presentation.detailLines.map((line) => (
            <p key={line} className="text-sm text-secondary-300">
              {line}
            </p>
          ))}
        </div>
      )}

      {loadError && (
        <p className="text-sm text-red-400" role="alert">
          {loadError}
        </p>
      )}

      {presentation.showStatusCard && (
        <BillingStatusCard
          status={billingStatus}
          loading={false}
          error={null}
          onRetry={() => void loadStatus()}
          returnLeagueId={leagueId}
        />
      )}

      {presentation.showBindCta && (
        <div
          className="rounded-lg border border-amber-700/60 bg-amber-950/40 px-4 py-3 space-y-3"
          data-testid="lsc-league-billing-paid-unbound"
          role="status"
        >
          {presentation.detailLines.map((line) => (
            <p key={line} className="text-sm text-amber-100">
              {line}
            </p>
          ))}
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            onClick={() => void handleBind()}
            disabled={bindLoading || leagueArchived}
            data-testid="lsc-league-bind-subscription-cta"
          >
            <Link2 className="h-4 w-4" aria-hidden />
            {bindLoading
              ? 'Binding subscription…'
              : presentation.primaryCta?.label || 'Bind subscription to this league'}
          </button>
          {bindError && (
            <p className="text-sm text-red-300" role="alert" data-testid="lsc-league-bind-error">
              {bindError}
            </p>
          )}
          {bindSuccess && (
            <p className="text-sm text-green-300" role="status" data-testid="lsc-league-bind-success">
              Subscription bound to this league. Refreshing billing status…
            </p>
          )}
        </div>
      )}

      {presentation.showCheckoutActions && (
        <div className="space-y-3" data-testid="lsc-league-checkout-section">
          <LeagueCheckoutActions leagueId={leagueId} />
        </div>
      )}

      {presentation.disabledBanner === 'lifecycle' &&
        !presentation.showCheckoutActions &&
        !presentation.showBindCta &&
        presentation.mode !== 'owner_managed' && (
          <BillingDisabledBanner variant="checkout" message={presentation.disabledReason} />
        )}

      <BillingSupportContact />
    </div>
  );
};

export default LeagueBillingPanel;
